import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import Detail from '../../../../types/detail/Detail';
import DetailWriteDTO from '../../../../types/detail/DetailWriteDTO';
import Page from '../../../../types/shared/Page';
import { CanManageSampleDefinitionsDirective } from '../../directives/can-manage-sample-definitions.directive';
import { DetailService } from '../../services/detail.service';
import { CreateDetailDialogComponent } from '../shared/details/create-detail-dialog/create-detail-dialog.component';
import { UpdateDetailDialogComponent } from '../shared/details/update-detail-dialog/update-detail-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-details',
    templateUrl: './details.component.html',
    styleUrls: [ './details.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateDetailDialogComponent,
        UpdateDetailDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        CanManageSampleDefinitionsDirective,
        Tooltip
    ]
})
export class DetailsComponent implements OnInit {
    public search = '';
    public details: Detail[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateDetailDialog = false;
    public displayUpdateDetailDialog = false;
    public selectedDetail: DetailWriteDTO = new DetailWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(
        private detailService: DetailService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component by loading details and setting up the search input.
     */
    public ngOnInit(): void {
        this.loadDetails();

        // Debounce search input to reduce excessive API calls
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset page when new search begins
            this.loadDetails();
        });
    }

    /**
     * Handles page change events from the table.
     *
     * @param event The event containing pagination information.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadDetails();
    }

    /**
     * Handles changes to the search input.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Opens the dialog to edit a detail.
     *
     * @param detail The detail to edit.
     */
    public editDetail(detail: Detail): void {
        this.selectedDetail = new DetailWriteDTO(detail);
        this.displayUpdateDetailDialog = true;
    }

    /**
     * Deletes a detail after user confirmation.
     *
     * @param detail The detail to delete.
     */
    public deleteDetail(detail: Detail): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the detail ${ detail.name }?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.detailService
                    .deleteDetail(detail.id)
                    .then(() => {
                        this.loadDetails();
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Detail deleted',
                            life: 3000
                        });
                    })
                    .catch((error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message,
                            life: 3000
                        });
                    });
            }
        });
    }

    /**
     * Opens the dialog to create a new detail.
     */
    public openCreateDetailDialog(): void {
        this.displayCreateDetailDialog = true;
    }

    /**
     * Reloads the details when called.
     */
    public reloadDetails(): void {
        this.loadDetails();
    }

    /**
     * Load paginated details from the server.
     */
    private loadDetails(): void {
        this.loading = true;
        const size = this.rows;
        const search = this.search || '';

        this.detailService
            .getDetails(search, this.currentPage, size)
            .then((result: Page<Detail>) => {
                this.details = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
